/**
 * KokoroTTSService — Main-thread TTS service backed by a Web Worker.
 *
 * This service is the primary interface for all TTS operations in Faana.
 * The model runs in a Web Worker (kokoro-tts.worker.js) so the UI thread
 * never blocks during ONNX model loading or audio synthesis.
 *
 * Performance architecture:
 *   The worker uses kokoro-js stream() — an async generator that yields one
 *   audio chunk per sentence as soon as it is synthesized. The main thread
 *   starts scheduling audio playback after the FIRST chunk arrives, rather
 *   than waiting for the entire text to be synthesized.
 *
 *   Multi-threaded ONNX WASM requires COOP/COEP HTTP headers:
 *     Cross-Origin-Opener-Policy: same-origin
 *     Cross-Origin-Embedder-Policy: require-corp
 *   See src/setupProxy.js (dev) and firebase.json (production).
 *
 * Usage:
 *   const tts = new KokoroTTSService();
 *   await tts.init((progress) => console.log(progress));
 *   tts.speak('Hello world', 'af_heart', {
 *     onSpeakingStart: () => console.log('started'),
 *     onSpeakingEnd: () => console.log('finished'),
 *   });
 */

import { createKokoroWorker } from './createKokoroWorker';

/**
 * Known Kokoro voice IDs.
 * Any voice name NOT in this set is treated as a legacy browser voice
 * and auto-migrated to DEFAULT_VOICE.
 *
 * Source: https://huggingface.co/onnx-community/Kokoro-82M-v1.0-ONNX
 * Verified voice IDs available in the model.
 */
const KOKORO_VOICE_IDS = new Set([
  // American female
  'af_heart', 'af_alloy', 'af_aoede', 'af_bella', 'af_jessica',
  'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky',
  // American male
  'am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam',
  'am_michael', 'am_onyx', 'am_orion', 'am_puck',
  // British female
  'bf_alice', 'bf_emma', 'bf_isabella', 'bf_lily',
  // British male
  'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis',
]);

// ---------------------------------------------------------------------------
// Internal LRU cache (~20 lines)
// ---------------------------------------------------------------------------

/**
 * Map-based LRU cache with configurable max size.
 * Uses delete + re-insert pattern for O(1) recency updates.
 *
 * Cache key pattern: "${text}::${voiceId}"
 * Cache value: { audio: Float32Array, sampleRate: number }
 */
class LRUCache {
  constructor(maxSize = 50) {
    this._cache = new Map();
    this._maxSize = maxSize;
  }

  get(key) {
    if (!this._cache.has(key)) return undefined;
    // Refresh recency: delete and re-insert
    const value = this._cache.get(key);
    this._cache.delete(key);
    this._cache.set(key, value);
    return value;
  }

  set(key, value) {
    if (this._cache.has(key)) {
      // Re-insert for recency
      this._cache.delete(key);
    } else if (this._cache.size >= this._maxSize) {
      // Evict least recently used (first/oldest entry in Map iteration order)
      this._cache.delete(this._cache.keys().next().value);
    }
    this._cache.set(key, value);
  }

  has(key) {
    return this._cache.has(key);
  }
}

// ---------------------------------------------------------------------------
// KokoroTTSService
// ---------------------------------------------------------------------------

export class KokoroTTSService {
  /**
   * Default voice: American female, warm coaching tone.
   * Used when voice is unset or is a legacy browser voice name.
   */
  static DEFAULT_VOICE = 'af_heart';

  constructor() {
    // Worker reference — null until init() is called
    this._worker = null;

    // Model state flags
    this._ready = false;
    this._loading = false;

    // Singleton init promise — reused on subsequent init() calls
    this._initPromise = null;

    // Progress callback stored during init
    this._progressCallback = null;

    // Resolve/reject functions for the init promise
    this._initResolve = null;
    this._initReject = null;

    // --- Streaming synthesis state ---

    // Web Audio API context for audio playback — created lazily on first speak()
    this._audioContext = null;

    // Scheduled start time for next audio segment (gapless playback)
    this._nextStartTime = 0;

    // Active AudioBufferSourceNodes (for stop())
    this._activeSources = [];

    // Whether TTS is currently speaking
    this._isSpeaking = false;

    // LRU cache for synthesized audio
    // Key: "${text}::${voiceId}", value: { audio: Float32Array, sampleRate }
    this._cache = new LRUCache(50);

    // Callbacks for the current speak() call
    this._callbacks = {};

    // Map of synthesis request id -> handler for streaming chunks.
    // Each entry: { onChunk(audio, sampleRate), onDone(), onError(err), text, voiceId }
    this._pendingRequests = new Map();

    // Number of audio chunks received but not yet ended (still in activeSources)
    // Used to detect when ALL chunks have finished playing.
    this._pendingChunkCount = 0;

    // Whether the stream is complete (done=true received from worker)
    this._streamDone = false;

    // Playback speed (applied via playbackRate.value in Web Audio API)
    this._speed = 1.0;

    // Playback volume (applied via GainNode.gain.value)
    this._volume = 1.0;

    // Whether onSpeakingStart has been called for the current speak()
    this._speakingStartFired = false;
  }

  // ---------------------------------------------------------------------------
  // Public API — Plan 01
  // ---------------------------------------------------------------------------

  /**
   * Initialize the TTS service by loading the Kokoro model in a Web Worker.
   *
   * Lazy initialization: the model is not downloaded until this is called.
   * Singleton: subsequent calls return the same promise — the model is only
   * loaded once per service instance.
   *
   * @param {function} [progressCallback] - Called with progress data as the
   *   model downloads. Signature: (data: { status, file, progress?, loaded?, total? }) => void
   * @returns {Promise<void>} Resolves when the model is ready to synthesize.
   */
  init(progressCallback) {
    if (this._initPromise) {
      // Already initializing or initialized — return the same promise (singleton)
      return this._initPromise;
    }

    this._loading = true;
    this._progressCallback = progressCallback || null;

    // Create the init promise with captured resolve/reject
    this._initPromise = new Promise((resolve, reject) => {
      this._initResolve = resolve;
      this._initReject = reject;
    });

    // Create worker and wire up message handler
    this._worker = createKokoroWorker();
    this._worker.onmessage = this._handleWorkerMessage.bind(this);

    // Detect WebGPU on main thread (workers often lack navigator.gpu)
    // and pass device hint to the worker for faster ONNX inference.
    this._detectDevice().then((device) => {
      console.log('[KokoroTTS] Detected ONNX device:', device);
      this._worker.postMessage({ type: 'init', device });
    });

    return this._initPromise;
  }

  /**
   * Whether the model is loaded and ready to synthesize audio.
   * @returns {boolean}
   */
  isReady() {
    return this._ready;
  }

  /**
   * Whether the model is currently downloading/initializing.
   * @returns {boolean}
   */
  isModelLoading() {
    return this._loading;
  }

  // ---------------------------------------------------------------------------
  // Public API — Plan 02
  // ---------------------------------------------------------------------------

  /**
   * Synthesize and speak text using the given voice.
   *
   * Pipeline:
   *   1. If not ready, return early (model still loading — caller should retry)
   *   2. Create AudioContext lazily on first call
   *   3. Stop current speech if already speaking
   *   4. Check LRU cache — if hit, schedule all chunks immediately
   *   5. If cache miss, post 'synthesize' to worker
   *   6. Worker streams chunks back via 'audio' messages (one per sentence)
   *   7. Each chunk is scheduled for gapless playback as it arrives
   *   8. onSpeakingStart fires when first chunk is scheduled
   *   9. onSpeakingEnd fires when last chunk finishes playing
   *
   * @param {string} text - Text to synthesize (may contain markdown)
   * @param {string} [voiceId] - Kokoro voice ID; defaults to DEFAULT_VOICE
   * @param {object} [callbacks] - { onSpeakingStart, onSpeakingEnd, onSpeakingError }
   */
  speak(text, voiceId, callbacks) {
    const resolvedVoice = this._resolveVoicePreset(voiceId);
    const cbs = callbacks || {};

    // If not ready, return immediately — caller should wait for model loading
    if (!this._ready) {
      console.log('[KokoroTTS] Model not ready — speak() called before init() completed');
      return;
    }

    // Ensure AudioContext exists and is running (PLAY-04)
    this._ensureAudioContext();

    // Stop current speech if already speaking (clean interruption)
    if (this._isSpeaking) {
      this.stop();
    }

    // Store callbacks for this speak() call
    this._callbacks = cbs;
    this._speakingStartFired = false;
    this._isSpeaking = true;
    this._streamDone = false;
    this._pendingChunkCount = 0;

    // Check LRU cache — the cache key is the full text (not per-chunk)
    // because the worker now streams the whole text as one request.
    const cacheKey = `${text}::${resolvedVoice}`;
    if (this._cache.has(cacheKey)) {
      // Cache hit — cached as array of chunks (from a previous streaming synthesis)
      const cachedChunks = this._cache.get(cacheKey);
      for (const cached of cachedChunks) {
        this._pendingChunkCount++;
        this._scheduleChunk(cached.audio, cached.sampleRate);
        this._pendingChunkCount--;
      }
      // All chunks scheduled synchronously — check completion
      this._checkSpeakingEnd();
      return;
    }

    // Cache miss — stream synthesis from worker.
    // The worker will send multiple 'audio' messages (one per sentence) followed
    // by a final 'audio' message with done=true.
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const collectedChunks = []; // Collect for caching

    this._pendingRequests.set(id, {
      onChunk: (audio, sampleRate) => {
        collectedChunks.push({ audio, sampleRate });
        this._pendingChunkCount++;
        this._scheduleChunk(audio, sampleRate);
      },
      onDone: () => {
        this._pendingRequests.delete(id);
        this._streamDone = true;
        // Cache the complete synthesized result for future calls
        if (collectedChunks.length > 0) {
          this._cache.set(cacheKey, collectedChunks);
        }
        // If all chunks have already finished playing, fire onSpeakingEnd
        this._checkSpeakingEnd();
      },
      onError: (err) => {
        this._pendingRequests.delete(id);
        this._streamDone = true;
        console.error('[KokoroTTS] speak() onError:', err.message);
        this._isSpeaking = false;
        if (this._callbacks && this._callbacks.onSpeakingError) {
          this._callbacks.onSpeakingError(err);
        }
      },
      text,
      voiceId: resolvedVoice,
    });

    this._worker.postMessage({
      type: 'synthesize',
      id,
      text,
      voiceId: resolvedVoice,
    });
  }

  /**
   * Stop current speech playback immediately.
   *
   * Stops all active AudioBufferSourceNodes, posts abort to worker,
   * clears pending requests and resets state.
   */
  stop() {
    // Stop all active audio sources
    this._activeSources.forEach(src => {
      try {
        src.stop();
      } catch (_) {
        // Already stopped — ignore
      }
    });
    this._activeSources = [];
    this._nextStartTime = 0;

    // Abort pending worker synthesis
    if (this._worker) {
      this._worker.postMessage({ type: 'abort' });
    }

    // Clear all pending synthesis requests
    this._pendingRequests.clear();
    this._pendingChunkCount = 0;
    this._streamDone = false;
    this._isSpeaking = false;

    // Fire onSpeakingEnd if callbacks registered
    if (this._callbacks && this._callbacks.onSpeakingEnd) {
      this._callbacks.onSpeakingEnd();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API — Streaming sentence queue
  // ---------------------------------------------------------------------------

  /**
   * Begin a streaming session — initialize AudioContext and state, but don't
   * send any text to the worker yet. Sentences are fed one at a time via
   * enqueueSentence() and processed sequentially (the worker can only handle
   * one synthesize request at a time).
   *
   * @param {string} [voiceId] - Kokoro voice ID; defaults to DEFAULT_VOICE
   * @param {object} [callbacks] - { onSpeakingStart, onSpeakingEnd, onSpeakingError }
   */
  beginStreaming(voiceId, callbacks) {
    const resolvedVoice = this._resolveVoicePreset(voiceId);
    const cbs = callbacks || {};

    if (!this._ready) {
      console.log('[KokoroTTS] Model not ready — beginStreaming() called before init() completed');
      return false;
    }

    this._ensureAudioContext();

    // Stop current speech if already speaking
    if (this._isSpeaking) {
      this.stop();
    }

    // Store state for streaming session
    this._callbacks = cbs;
    this._speakingStartFired = false;
    this._isSpeaking = true;
    this._streamDone = false;
    this._pendingChunkCount = 0;

    // Streaming queue state
    this._streamingQueue = [];
    this._streamingSynthesizing = false;
    this._streamingEnded = false;
    this._streamingVoice = resolvedVoice;

    return true;
  }

  /**
   * Push a sentence into the streaming queue. If the worker is idle,
   * immediately start synthesizing the next sentence.
   *
   * @param {string} sentence - A complete sentence to synthesize.
   */
  enqueueSentence(sentence) {
    if (!sentence || !sentence.trim()) return;
    this._streamingQueue.push(sentence.trim());

    // If worker is idle, kick off synthesis
    if (!this._streamingSynthesizing) {
      this._synthesizeNextInQueue();
    }
  }

  /**
   * Mark the streaming session as ended — no more sentences will be enqueued.
   * If the queue is empty and the worker is idle, finalize immediately.
   */
  endStreaming() {
    this._streamingEnded = true;

    if (this._streamingQueue.length === 0 && !this._streamingSynthesizing) {
      this._streamDone = true;
      this._checkSpeakingEnd();
    }
  }

  /**
   * Pop the next sentence from the queue and send it to the worker.
   * When synthesis completes (onDone), this method calls itself recursively
   * to process the next queued sentence. Each sentence's audio chunks are
   * scheduled via _scheduleChunk for gapless playback.
   */
  _synthesizeNextInQueue() {
    if (this._streamingQueue.length === 0) {
      this._streamingSynthesizing = false;
      if (this._streamingEnded) {
        this._streamDone = true;
        this._checkSpeakingEnd();
      }
      return;
    }

    this._streamingSynthesizing = true;
    const sentence = this._streamingQueue.shift();
    const id = `stream-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    this._pendingRequests.set(id, {
      onChunk: (audio, sampleRate) => {
        this._pendingChunkCount++;
        this._scheduleChunk(audio, sampleRate);
      },
      onDone: () => {
        this._pendingRequests.delete(id);
        this._streamingSynthesizing = false;
        // Process next sentence in queue
        this._synthesizeNextInQueue();
      },
      onError: (err) => {
        this._pendingRequests.delete(id);
        this._streamingSynthesizing = false;
        console.error('[KokoroTTS] Streaming sentence synthesis error:', err.message);
        // Continue with next sentence despite error
        this._synthesizeNextInQueue();
      },
      text: sentence,
      voiceId: this._streamingVoice,
    });

    this._worker.postMessage({
      type: 'synthesize',
      id,
      text: sentence,
      voiceId: this._streamingVoice,
    });
  }

  /**
   * Preview a voice by synthesizing a short sample.
   * Called by Phase 8 VoiceSettings for the "test voice" button.
   *
   * @param {string} voiceId - Kokoro voice ID to preview
   */
  previewVoice(voiceId) {
    const sampleText = 'Hello, how can I help you today?';
    return this.speak(sampleText, voiceId, {});
  }

  /**
   * Set playback speed (applied via Web Audio API playbackRate).
   * Does not affect synthesis — speed is applied at playback time.
   *
   * @param {number} speed - Playback rate multiplier (e.g. 1.0 = normal, 1.5 = 50% faster)
   */
  setSpeed(speed) {
    this._speed = speed;
  }

  /**
   * Set playback volume (applied via Web Audio API GainNode).
   * Does not affect synthesis — volume is applied at playback time.
   *
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setVolume(volume) {
    this._volume = volume;
  }

  // ---------------------------------------------------------------------------
  // Internal — Worker message handling
  // ---------------------------------------------------------------------------

  /**
   * Route messages from the worker to the appropriate handler.
   * @param {MessageEvent} event
   */
  _handleWorkerMessage(event) {
    const { type, id, ...rest } = event.data;

    switch (type) {
      case 'progress':
        if (this._progressCallback) {
          this._progressCallback(rest);
        }
        break;

      case 'ready':
        this._ready = true;
        this._loading = false;
        if (this._initResolve) {
          this._initResolve();
        }
        break;

      case 'audio':
        // Route to the pending request handler
        if (this._pendingRequests.has(id)) {
          const handler = this._pendingRequests.get(id);
          if (rest.done) {
            handler.onDone();
          } else {
            handler.onChunk(rest.audio, rest.sampleRate);
          }
        }
        break;

      case 'error':
        if (id === null || id === undefined) {
          // Init error
          this._loading = false;
          this._ready = false;
          if (this._initReject) {
            this._initReject(new Error(rest.message));
          }
        } else {
          // Synthesis error
          if (this._pendingRequests.has(id)) {
            const handler = this._pendingRequests.get(id);
            handler.onError(new Error(rest.message));
          }
        }
        break;

      default:
        // Unknown message type — ignore
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Internal — Device detection
  // ---------------------------------------------------------------------------

  /**
   * Detect best ONNX backend from the main thread.
   * Web Workers often lack navigator.gpu, so detection must happen here.
   * WebGPU is ~10x faster than WASM for ONNX inference.
   */
  async _detectDevice() {
    try {
      if (typeof navigator !== 'undefined' && navigator.gpu) {
        const adapter = await navigator.gpu.requestAdapter();
        if (adapter) {
          return 'webgpu';
        }
      }
    } catch (_) {
      // WebGPU not available
    }
    return 'wasm';
  }

  // ---------------------------------------------------------------------------
  // Internal — AudioContext lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Ensure an AudioContext exists and is running.
   *
   * Creates AudioContext only on first call (PLAY-04 — lazy creation on user gesture).
   * Handles iOS Safari "suspended" and WebKit Bug 237878 "interrupted" states.
   */
  _ensureAudioContext() {
    if (!this._audioContext) {
      this._audioContext = new AudioContext();

      // Handle state changes — resume if interrupted (WebKit Bug 237878)
      this._audioContext.onstatechange = () => {
        if (this._audioContext.state === 'interrupted') {
          this._audioContext.resume();
        }
      };
    }

    // Resume if suspended (iOS Safari autoplay policy)
    const state = this._audioContext.state;
    if (state === 'suspended' || state === 'interrupted') {
      this._audioContext.resume();
    }
  }

  // ---------------------------------------------------------------------------
  // Internal — Audio scheduling (PLAY-01 gapless playback)
  // ---------------------------------------------------------------------------

  /**
   * Schedule an audio chunk for gapless playback via Web Audio API.
   *
   * Uses AudioBufferSourceNode.start(nextStartTime) to pre-schedule chunks
   * so they play back-to-back with no gaps between them.
   *
   * In streaming mode, chunks arrive one at a time (per sentence) as the worker
   * synthesizes them. Each chunk is scheduled as soon as it arrives — the next
   * chunk is scheduled at the end time of the previous one, creating a gapless
   * sequence even though the chunks arrive asynchronously.
   *
   * @param {Float32Array} audioFloat32 - Raw PCM audio data
   * @param {number} sampleRate - Audio sample rate (typically 24000 Hz)
   */
  _scheduleChunk(audioFloat32, sampleRate) {
    const ctx = this._audioContext;
    if (!ctx) return;

    // Create AudioBuffer from Float32Array
    const buffer = ctx.createBuffer(1, audioFloat32.length, sampleRate);
    buffer.getChannelData(0).set(audioFloat32);

    // Create source node
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this._speed;

    // Create gain node for volume control
    const gain = ctx.createGain();
    gain.gain.value = this._volume;

    // Connect: source -> gain -> destination
    source.connect(gain);
    gain.connect(ctx.destination);

    // Gapless scheduling: start at nextStartTime or currentTime (whichever is later)
    const startTime = Math.max(ctx.currentTime, this._nextStartTime);
    source.start(startTime);

    // Advance nextStartTime by this chunk's duration (accounting for speed)
    this._nextStartTime = startTime + buffer.duration / this._speed;

    // Track for stop()
    this._activeSources.push(source);

    // Fire onSpeakingStart on first chunk scheduled
    if (!this._speakingStartFired) {
      this._speakingStartFired = true;
      if (this._callbacks && this._callbacks.onSpeakingStart) {
        this._callbacks.onSpeakingStart();
      }
    }

    // When chunk ends: remove from active sources, check if all done
    source.onended = () => {
      const idx = this._activeSources.indexOf(source);
      if (idx !== -1) {
        this._activeSources.splice(idx, 1);
      }
      this._pendingChunkCount--;
      this._checkSpeakingEnd();
    };
  }

  // ---------------------------------------------------------------------------
  // Internal — Completion check
  // ---------------------------------------------------------------------------

  /**
   * Check if all synthesis and playback is complete.
   *
   * Speech is done when:
   *   1. The worker stream is complete (done=true received)
   *   2. All pending audio chunks have been scheduled AND played back
   *      (_pendingChunkCount reaches 0 after _streamDone is set)
   *   3. No more active AudioBufferSourceNodes
   *
   * This is called:
   *   - From source.onended (a chunk finished playing)
   *   - From onDone (worker stream complete)
   *   - From speak() when cache hit
   */
  _checkSpeakingEnd() {
    if (
      this._isSpeaking &&
      this._streamDone &&
      this._pendingChunkCount === 0 &&
      this._activeSources.length === 0
    ) {
      this._isSpeaking = false;
      if (this._callbacks && this._callbacks.onSpeakingEnd) {
        this._callbacks.onSpeakingEnd();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Internal — Voice resolution
  // ---------------------------------------------------------------------------

  /**
   * Resolve a voice name to a valid Kokoro voice ID.
   *
   * If the name is a known Kokoro voice ID, return it unchanged.
   * Otherwise, treat it as a legacy browser voice name (e.g. "Google US English")
   * and return the default voice, auto-migrating the user to Kokoro.
   *
   * @param {string|null|undefined} voiceName
   * @returns {string} A valid Kokoro voice ID
   */
  _resolveVoicePreset(voiceName) {
    if (voiceName && KOKORO_VOICE_IDS.has(voiceName)) {
      return voiceName;
    }
    return KokoroTTSService.DEFAULT_VOICE;
  }
}
